import { useCallback, useId, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  IconButton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { EmptyStateMessage, Section, SectionCard, SectionHeader } from '@/components/common';
import { RichTextEditor, RichTextReadOnly } from '@/components/common/RichTextEditor';
import { formatDate } from '@/utils/dateFormatter';
import { getErrorMessage } from '@/utils/errorMessage';
import { useQueryClient } from '@tanstack/react-query';
import {
  seniorFeedbackKeys,
  useSeniorFeedbacks,
  useEligibleSeniorReviewers,
  useAddSeniorReviewer,
  useUpdateSeniorFeedback,
  useDeleteSeniorReviewer,
} from '@/queries/useSeniorFeedback';
import { jobEndKeys } from '@/queries/useJobEnds';
import { useAuthStore } from '@/stores/useAuthStore';
import type { EligibleSeniorReviewer, JobEndDetail } from '@/api/types/jobEnd.types';

interface SeniorTeamFeedbackSectionProps {
  jobEndId: number;
  earliestDeadline: string | null;
  access: 'edit' | 'read';
  role: string;
  rowVersion?: string;
}

export function SeniorTeamFeedbackSection({
  jobEndId,
  earliestDeadline,
  access,
  role,
  rowVersion,
}: SeniorTeamFeedbackSectionProps) {
  const isCollator = (role === 'Collator' || role === 'Delegate') && access === 'edit';
  // Same edit guard as the Collator: the read-only branch below already covers this, but relying on
  // that alone is one refactor away from showing a reviewer a SAVE button on a submitted Job End.
  const isSeniorReviewer = role === 'SeniorTeamReviewer' && access === 'edit';
  const isReadOnly = access === 'read' || role === 'Appraisee';

  return (
    <SeniorTeamFeedbackContent
      jobEndId={jobEndId}
      earliestDeadline={earliestDeadline}
      isCollator={isCollator}
      isSeniorReviewer={isSeniorReviewer}
      isReadOnly={isReadOnly}
      rowVersion={rowVersion}
    />
  );
}

interface SeniorTeamFeedbackContentProps {
  jobEndId: number;
  earliestDeadline: string | null;
  isCollator: boolean;
  isSeniorReviewer: boolean;
  isReadOnly: boolean;
  rowVersion?: string;
}

function SeniorTeamFeedbackContent({
  jobEndId,
  earliestDeadline,
  isCollator,
  isSeniorReviewer,
  isReadOnly,
  rowVersion,
}: SeniorTeamFeedbackContentProps) {
  // Server-computed so the config and working-day maths live in one place. GetJobEnd always populates
  // it; today is only a last resort that keeps the picker usable (the server still has the final say).
  const minDate = earliestDeadline?.slice(0, 10) ?? new Date().toISOString().slice(0, 10);

  const deadlineInputId = useId();
  const [selectedReviewer, setSelectedReviewer] = useState<EligibleSeniorReviewer | null>(null);
  const [deadline, setDeadline] = useState('');
  const [reviewerError, setReviewerError] = useState(false);
  const [deadlineError, setDeadlineError] = useState<string | false>(false);
  const [localComments, setLocalComments] = useState<Map<number, string>>(new Map());
  const [commentErrors, setCommentErrors] = useState<Set<number>>(new Set());
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: '' });

  const queryClient = useQueryClient();
  const currentStaffId = useAuthStore((s) => s.permissions?.staffId);

  const { data: rawFeedbacks } = useSeniorFeedbacks(jobEndId);
  const feedbacks = rawFeedbacks ?? [];
  const { data: eligibleReviewers = [] } = useEligibleSeniorReviewers(jobEndId, isCollator);

  // Clear local comment cache when feedbacks change externally (e.g., after refetch/409 refresh).
  // Uses "adjusting state during render" pattern — React re-renders once then stabilizes.
  const [prevRawFeedbacks, setPrevRawFeedbacks] = useState(rawFeedbacks);
  if (prevRawFeedbacks !== rawFeedbacks) {
    setPrevRawFeedbacks(rawFeedbacks);
    setLocalComments(new Map());
  }
  const { mutate: addReviewer, isPending: isAdding } = useAddSeniorReviewer();
  const { mutate: updateFeedback, isPending: isSaving } = useUpdateSeniorFeedback();
  const { mutate: deleteReviewer } = useDeleteSeniorReviewer();
  const invalidateFeedbacks = () => {
    queryClient.invalidateQueries({ queryKey: seniorFeedbackKeys.byJobEnd(jobEndId) });
    queryClient.invalidateQueries({ queryKey: seniorFeedbackKeys.eligibleReviewers(jobEndId) });
  };

  const handleCommentChange = useCallback((feedbackId: number, html: string) => {
    setLocalComments((prev) => {
      const next = new Map(prev);
      next.set(feedbackId, html);
      return next;
    });
    setCommentErrors((prev) => {
      if (!prev.has(feedbackId)) return prev;
      const next = new Set(prev);
      next.delete(feedbackId);
      return next;
    });
  }, []);

  const handleAddReviewer = async () => {
    // The floor moves with the server's date, so a page left open overnight or over a weekend can be
    // offering a date the server now rejects. Re-read it before validating, as the footer does before
    // submitting, so the user is corrected here rather than by a silent request failure.
    await queryClient.refetchQueries({ queryKey: jobEndKeys.detail(String(jobEndId)) });
    const refreshed = queryClient.getQueryData<JobEndDetail>(jobEndKeys.detail(String(jobEndId)));
    const effectiveMinDate = refreshed?.seniorFeedbackEarliestDeadline?.slice(0, 10) ?? minDate;

    const hasReviewerError = !selectedReviewer;
    const deadlineErrorMsg = !deadline
      ? 'This field is required'
      : deadline < effectiveMinDate
        ? `Deadline must be ${formatDate(effectiveMinDate)} or later`
        : false;
    setReviewerError(hasReviewerError);
    setDeadlineError(deadlineErrorMsg);
    if (hasReviewerError || deadlineErrorMsg) return;
    const reviewerName = `${selectedReviewer.firstName} ${selectedReviewer.lastName}`;
    addReviewer(
      { jobEndId, request: { reviewerStaffId: selectedReviewer.staffId, dueDate: deadline } },
      {
        onSuccess: () => {
          setSelectedReviewer(null);
          setDeadline('');
          invalidateFeedbacks();
          setSnackbar({ open: true, message: `Senior Team Feedback request has been sent to ${reviewerName}` });
        },
        // Only 401/403/409 are handled globally, so without this a rejected deadline would leave the
        // button looking like it did nothing at all.
        onError: (error) => setDeadlineError(getErrorMessage(error)),
      }
    );
  };

  const handleReviewerChange = (_: unknown, value: EligibleSeniorReviewer | null) => {
    setSelectedReviewer(value);
    if (value) setReviewerError(false);
  };

  const handleDeadlineChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDeadline(e.target.value);
    if (e.target.value) setDeadlineError(false);
  };

  const handleSave = (feedbackId: number) => {
    const comments = localComments.get(feedbackId);
    if (comments === undefined || comments.replace(/<[^>]*>/g, '').trim() === '') {
      setCommentErrors((prev) => new Set(prev).add(feedbackId));
      return;
    }
    updateFeedback(
      { feedbackId, request: { comments }, rowVersion, jobEndId },
      {
        onSuccess: () => {
          invalidateFeedbacks();
          setSnackbar({ open: true, message: 'Senior team feedback saved successfully' });
        },
      }
    );
  };

  const handleBlurSave = (feedbackId: number) => {
    const comments = localComments.get(feedbackId);
    if (comments === undefined) return;
    if (comments.replace(/<[^>]*>/g, '').trim() === '') return;
    updateFeedback(
      { feedbackId, request: { comments }, rowVersion, jobEndId },
      { onSuccess: invalidateFeedbacks }
    );
  };

  const handleDelete = (feedbackId: number) => {
    deleteReviewer({ feedbackId, jobEndId }, { onSuccess: invalidateFeedbacks });
  };

  const hasErrors = reviewerError || !!deadlineError || commentErrors.size > 0;

  const displayFeedbacks = feedbacks;

  return (
    <Section>
      <SectionHeader error={hasErrors}>SENIOR TEAM FEEDBACK</SectionHeader>
      <SectionCard>
        {isCollator && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
            <Box sx={{ width: { xs: '100%', md: 300 } }}>
              <Typography variant="body1" color={reviewerError ? 'error' : 'text.secondary'} display="block">
                Senior Reviewer
              </Typography>
              <Autocomplete
                size="small"
                options={eligibleReviewers}
                getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                value={selectedReviewer}
                onChange={handleReviewerChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select person"
                    variant="standard"
                    error={reviewerError}
                    helperText={reviewerError ? 'This field is required' : ''}
                  />
                )}
              />
            </Box>
            <Box sx={{ width: { xs: '100%', md: 180 } }}>
              <Typography
                component="label"
                htmlFor={deadlineInputId}
                variant="body1"
                color={deadlineError ? 'error' : 'text.secondary'}
                display="block"
              >
                Deadline
              </Typography>
              <TextField
                type="date"
                size="small"
                variant="standard"
                value={deadline}
                onChange={handleDeadlineChange}
                slotProps={{ htmlInput: { id: deadlineInputId, min: minDate } }}
                error={!!deadlineError}
                helperText={deadlineError || ''}
                // The helper text is not a live region by default, so a rejected deadline would never
                // be announced (accessibility.md requires errors to reach screen readers).
                FormHelperTextProps={{ role: 'alert' }}
                fullWidth
              />
            </Box>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<AddIcon />}
              disabled={isAdding}
              onClick={handleAddReviewer}
              sx={{ ml: { md: 'auto' }, mt: '24px' }}
            >
              ADD REVIEWER
            </Button>
          </Box>
        )}


        {displayFeedbacks.length === 0 && (
          <EmptyStateMessage message="No senior team feedback available." />
        )}

        {displayFeedbacks.length > 0 && isReadOnly && (
          <ReadOnlyTable feedbacks={displayFeedbacks} />
        )}

        {displayFeedbacks.length > 0 && !isReadOnly && (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 160 }}>SENIOR REVIEWER</TableCell>
                  <TableCell sx={{ width: 100 }}>DEADLINE</TableCell>
                  <TableCell>COMMENTS</TableCell>
                  <TableCell sx={{ width: 140, pr: '60px' }} align="right">ACTIONS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayFeedbacks.map((feedback) => {
                  const isOwnRow = isSeniorReviewer && feedback.reviewerStaffId === currentStaffId;
                  const canEditRow = isCollator || isOwnRow;
                  const canDeleteRow = isCollator;
                  const currentComments = localComments.get(feedback.id) ?? feedback.comments;
                  const hasUnsavedChanges = localComments.has(feedback.id) && localComments.get(feedback.id) !== feedback.comments;
                  const hasCommentError = commentErrors.has(feedback.id);

                  return (
                    <TableRow key={feedback.id}>
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        <Typography variant="body2">{feedback.reviewerName}</Typography>
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        <Typography variant="body2">{formatDate(feedback.dueDate)}</Typography>
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'top', width: 570 }}>
                        {canEditRow ? (
                          <Box>
                            <Box
                              sx={hasCommentError ? { border: 1, borderColor: 'error.main', borderRadius: 1 } : undefined}
                              onBlur={isCollator ? () => handleBlurSave(feedback.id) : undefined}
                            >
                              <RichTextEditor
                                content={currentComments}
                                onChange={(html) => handleCommentChange(feedback.id, html)}
                                placeholder="Enter feedback comments..."
                              />
                            </Box>
                            {hasCommentError && (
                              <Typography variant="caption" color="error">
                                This field is required
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <RichTextReadOnly content={feedback.comments} />
                        )}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', gap: 1 }}>
                          {canEditRow && !isCollator && (
                            <Button
                              variant="contained"
                              color="success"
                              size="small"
                              disabled={isSaving || !hasUnsavedChanges}
                              onClick={() => handleSave(feedback.id)}
                            >
                              SAVE
                            </Button>
                          )}
                          {canDeleteRow && (
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => handleDelete(feedback.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </SectionCard>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setSnackbar({ open: false, message: '' })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Section>
  );
}

function ReadOnlyTable({
  feedbacks,
}: {
  feedbacks: { id: number; reviewerName: string; dueDate: string; comments: string }[];
}) {
  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 200 }}>SENIOR REVIEWER</TableCell>
            <TableCell sx={{ width: 100, whiteSpace: 'nowrap' }}>DEADLINE</TableCell>
            <TableCell>COMMENTS</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {feedbacks.map((feedback) => (
            <TableRow key={feedback.id}>
              <TableCell>
                <Typography variant="body2">{feedback.reviewerName}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{formatDate(feedback.dueDate)}</Typography>
              </TableCell>
              <TableCell>
                <RichTextReadOnly content={feedback.comments} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default SeniorTeamFeedbackSection;
